import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { useNavigate } from "react-router-dom";
import { GridLoader } from "react-spinners";
import {
  buscarSumarizacaoIcmsApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import { fmtMesAno, formatarCnpj, sumarizacaoEmAndamento } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import { formatarQuantidadeLancamentos } from "../Detalhe/utils/formatarQuantidade.util";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";
import Pagination from "../../../components/Pagination";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro from "../components/SelectFiltro";
import {
  usePermissoes,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";
import ModalReprocessarCat42 from "./ModalReprocessarCat42";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

function competenciaYm(competencia) {
  return String(competencia || "").slice(0, 7);
}

/** Reagrupa as linhas (NCM + competência) só por competência — soma dos NCMs do mês. */
function agregarPorCompetencia(linhas) {
  const porMes = new Map();
  for (const linha of linhas) {
    const ym = competenciaYm(linha.competencia);
    if (!porMes.has(ym)) {
      porMes.set(ym, {
        competencia: ym,
        ncms: new Set(),
        ressarcimento: 0,
        complemento: 0,
        creditoArt271: 0,
        qtdEstoqueFinal: 0,
        vlEstoqueFinal: 0,
      });
    }
    const acc = porMes.get(ym);
    acc.ncms.add(linha.ncm);
    acc.ressarcimento += Number(linha.vlRessarcimento) || 0;
    acc.complemento += Number(linha.vlComplemento) || 0;
    acc.creditoArt271 += Number(linha.vlCreditoOperacaoPropria) || 0;
    acc.qtdEstoqueFinal += Number(linha.qtdEstoqueFinal) || 0;
    acc.vlEstoqueFinal += Number(linha.vlEstoqueFinal) || 0;
  }
  return Array.from(porMes.values())
    .map((acc) => ({
      competencia: acc.competencia,
      itens: acc.ncms.size,
      ressarcimento: acc.ressarcimento,
      complemento: acc.complemento,
      creditoArt271: acc.creditoArt271,
      qtdEstoqueFinal: acc.qtdEstoqueFinal,
      vlEstoqueFinal: acc.vlEstoqueFinal,
    }))
    .sort((a, b) => a.competencia.localeCompare(b.competencia));
}

/**
 * Itens com estoque final: por NCM, olha só a competência MAIS RECENTE em que ele
 * aparece (saldo corrido não é somável entre meses) e conta quantos ainda têm saldo>0
 * na última movimentação da apuração inteira.
 */
function contarItensComEstoqueFinal(linhas) {
  const ultimaPorNcm = new Map();
  for (const linha of linhas) {
    const ym = competenciaYm(linha.competencia);
    const atual = ultimaPorNcm.get(linha.ncm);
    if (!atual || ym > atual.ym) {
      ultimaPorNcm.set(linha.ncm, { ym, qtd: Number(linha.qtdEstoqueFinal) || 0 });
    }
  }
  let count = 0;
  for (const { qtd } of ultimaPorNcm.values()) {
    if (qtd > 0) count += 1;
  }
  return count;
}

/**
 * Conteúdo da apuração CAT-42 (KPIs + tabela por competência + ações), reaproveitado
 * tanto pela rota standalone (`/detalhe/cat42`) quanto pela aba "ICMS Cat-42" da
 * Apuração ICMS (TXM-418) — recebe `apuracaoId` explícito em vez de ler da URL.
 */
export default function ApuracaoCat42Conteudo({
  apuracaoId,
  mostrarCabecalhoPagina = true,
  mostrarDashboard = true,
  onTotaisChange,
}) {
  const id = apuracaoId;
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState(null);
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [modalReprocessarAberto, setModalReprocessarAberto] = useState(false);
  const [porCompetenciaPagina, setPorCompetenciaPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoIcmsApuracao(id);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setDados(res);
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [id]);

  const linhas = useMemo(() => dados?.linhas ?? [], [dados]);

  const competenciasDisponiveis = useMemo(() => {
    const set = new Set(linhas.map((linha) => competenciaYm(linha.competencia)));
    return Array.from(set).sort();
  }, [linhas]);

  const competenciaOpcoes = useMemo(
    () => [
      { value: "", label: "Todas" },
      ...competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) })),
    ],
    [competenciasDisponiveis]
  );

  /** TXM-418 — apuração ainda é de 1 CNPJ (multi-CNPJ é o TXM-601); filtro equaliza com as
   * demais abas, hoje com uma opção só. */
  const cnpjApuracao = dados?.apuracao?.cnpj || "";
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpjApuracao ? [{ value: cnpjApuracao, label: formatarCnpj(cnpjApuracao) }] : []),
    ],
    [cnpjApuracao]
  );

  const linhasFiltradas = useMemo(() => {
    if (cnpjFiltro && cnpjFiltro !== cnpjApuracao) return [];
    if (!competenciaFiltro) return linhas;
    return linhas.filter((linha) => competenciaYm(linha.competencia) === competenciaFiltro);
  }, [linhas, cnpjApuracao, cnpjFiltro, competenciaFiltro]);

  const porCompetencia = useMemo(() => agregarPorCompetencia(linhasFiltradas), [linhasFiltradas]);
  const totais = useMemo(
    () =>
      linhasFiltradas.reduce(
        (acc, linha) => {
          acc.ressarcimento += Number(linha.vlRessarcimento) || 0;
          acc.complemento += Number(linha.vlComplemento) || 0;
          acc.creditoArt271 += Number(linha.vlCreditoOperacaoPropria) || 0;
          return acc;
        },
        { ressarcimento: 0, complemento: 0, creditoArt271: 0 }
      ),
    [linhasFiltradas]
  );
  const itensComEstoqueFinal = useMemo(
    () => contarItensComEstoqueFinal(linhasFiltradas),
    [linhasFiltradas]
  );

  // TXM-474 — quando embutido como aba (mostrarDashboard=false), os cards saem daqui e são
  // renderizados pelo container da tela unificada (fica fora das abas, TXM-418 seção 1).
  useEffect(() => {
    onTotaisChange?.({ ...totais, itensComEstoqueFinal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais, itensComEstoqueFinal]);

  /**
   * TXM-418 (2026-09-16) — antes disso, os botões só mostravam "será entregue em breve" e
   * nunca buscavam o arquivo de volta. Desde 2026-10-02 cada botão tem sua própria instância
   * de `useExportacaoAssincrona` (acompanha o job pelo correlationId, mostra progresso e
   * baixa quando o servidor diz `concluido`) — Kardex e Arquivo Digital são variantes
   * independentes no servidor, então podem rodar ao mesmo tempo.
   */
  const notificarExportacao = ({ severity, msg }) => setInfoSnack({ open: true, msg, severity });
  const exportacaoKardex = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "lancamentos",
    enfileirar: (formato) => exportarSumarizacaoApuracao(id, { tipo: "lancamentos", formato }),
    onFinalizar: notificarExportacao,
  });
  const exportacaoArquivoDigital = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "arquivo_digital",
    enfileirar: () => exportarSumarizacaoApuracao(id, { tipo: "arquivo_digital" }),
    onFinalizar: notificarExportacao,
  });

  function handleReprocessarSucesso(msg) {
    setInfoSnack({ open: true, msg, severity: "success" });
  }

  function handleReprocessarErro(msg) {
    setInfoSnack({ open: true, msg, severity: "error" });
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro || !dados) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro || "Não foi possível carregar a sumarização."}
        </Alert>
        {mostrarCabecalhoPagina && (
          <button
            type="button"
            className={styles.linkVoltar}
            style={{ marginTop: 16 }}
            onClick={() => navigate("/relatorios/apuracoes")}
          >
            ← Voltar para Apurações
          </button>
        )}
      </Box>
    );
  }

  const ap = dados.apuracao || {};

  // TXM-418 — aba embutida na apuração ICMS: mesmo padrão visual das demais abas
  // (ApuracaoCardShell "bare" + SelectFiltro + tableStyles.accordion_table), sem o
  // card/página próprios (pageWrap/container) que a rota standalone usa — evitava
  // um card dentro de outro card quando a aba já vive dentro do card único da tela
  // unificada. A rota standalone (/detalhe/cat42) não é afetada: ela sempre passa
  // mostrarCabecalhoPagina=true e continua caindo no bloco original, abaixo, intacto.
  if (!mostrarCabecalhoPagina) {
    return (
      <>
        <ApuracaoCardShell
          bare
          conteudoComScrollProprio
          Icone={Inventory2Icon}
          titulo="Apuração por competência"
          tituloSx={{ fontSize: "1.25rem" }}
          acaoHeader={
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
              <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                {porCompetencia.length} registros no filtro
              </Typography>
              <BotaoExportacaoProgresso
                progresso={exportacaoKardex}
                rotulo="Exportar XLSX"
                variant="outlined"
                size="small"
                disabled={!podeExportar}
                onClick={() => exportacaoKardex.iniciar("xlsx")}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              />
              <BotaoExportacaoProgresso
                progresso={exportacaoArquivoDigital}
                rotulo="Baixar arquivo digital"
                variant="outlined"
                size="small"
                disabled={!podeExportar}
                onClick={() => exportacaoArquivoDigital.iniciar()}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => setModalReprocessarAberto(true)}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
              >
                Reprocessar
              </Button>
            </Box>
          }
          filtros={
            <>
              <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
              <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCnpjFiltro("");
                  setCompetenciaFiltro("");
                }}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
              >
                Limpar filtro
              </Button>
            </>
          }
          paginacao={
            porCompetencia.length > 0 && (
              <Pagination
                paginationKey="detalhe-cat42-competencia"
                data={porCompetencia}
                filteredData={porCompetenciaPagina}
                setFilteredData={setPorCompetenciaPagina}
              />
            )
          }
        >
          {porCompetencia.length === 0 ? (
            <p className={styles.emptyMsg}>Nenhuma competência encontrada para esta apuração.</p>
          ) : (
              <div style={{ margin: "8px 0 0", overflow: "auto" }}>
              <table className={tableStyles.accordion_table} style={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">CNPJ</th>
                    <th scope="col">Competência</th>
                    <th scope="col" style={NUM_TH}>Itens</th>
                    <th scope="col" style={NUM_TH}>Qtd estoque final</th>
                    <th scope="col" style={NUM_TH}>ICMS estoque final</th>
                    <th scope="col" style={NUM_TH}>Ressarcimento</th>
                    <th scope="col" style={NUM_TH}>Complemento</th>
                    <th scope="col" style={NUM_TH}>Art. 271</th>
                    <th scope="col" style={{ textAlign: "center" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {porCompetenciaPagina.map((linha) => (
                    <tr key={linha.competencia}>
                      <td>{formatarCnpj(cnpjApuracao)}</td>
                      <td>{fmtMesAno(linha.competencia)}</td>
                      <td style={NUM_TD}>{formatarQuantidadeLancamentos(linha.itens)}</td>
                      <td style={NUM_TD}>{formatarQuantidadeLancamentos(linha.qtdEstoqueFinal)}</td>
                      <td style={NUM_TD}>{formatarMoedaBRL(linha.vlEstoqueFinal)}</td>
                      <td style={{ ...NUM_TD, color: "#2e7d32" }}>{formatarMoedaBRL(linha.ressarcimento)}</td>
                      <td style={{ ...NUM_TD, color: "#c62828" }}>{formatarMoedaBRL(linha.complemento)}</td>
                      <td style={NUM_TD}>{formatarMoedaBRL(linha.creditoArt271)}</td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          type="button"
                          className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                          onClick={() =>
                            navigate(`/relatorios/apuracoes/${id}/detalhe/cat42/lancamentos?competencia=${linha.competencia}`)
                          }
                        >
                          Lançamentos
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
          )}
        </ApuracaoCardShell>

        <ModalReprocessarCat42
          open={modalReprocessarAberto}
          onClose={() => setModalReprocessarAberto(false)}
          onSuccess={handleReprocessarSucesso}
          onError={handleReprocessarErro}
          apuracao={ap}
          idApuracao={id}
        />

        <Snackbar
          open={infoSnack.open}
          autoHideDuration={4000}
          onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
        >
          <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.container}>
        {mostrarCabecalhoPagina && (
          <>
            <button
              type="button"
              className={styles.linkVoltar}
              onClick={() => navigate("/relatorios/apuracoes")}
            >
              ← Voltar para Apurações
            </button>

            <header className={`${styles.pageHeader} ${styles.pageHeaderAcoesNaBase}`}>
              <div className={styles.headerTitleBlock}>
                <h1 className={styles.pageTitle}>
                  Apuração ICMS-ST CAT-42 — {ap.empresa || "—"}
                </h1>
                <p className={styles.pageSubtitle}>
                  Apuração #{ap.id ?? id} · {formatarCnpj(ap.cnpj)} · {fmtMesAno(ap.periodoInicio)} a{" "}
                  {fmtMesAno(ap.periodoFim)}
                </p>
                <span className={styles.badgeTipo}>CAT-42 · Ressarcimento/Complemento ICMS-ST</span>
              </div>
              <div className={styles.headerActions}>
                <BotaoExportacaoProgresso
                  como="nativo"
                  className={styles.btnHeader}
                  progresso={exportacaoKardex}
                  rotulo="Exportar XLSX"
                  disabled={!podeExportar}
                  onClick={() => exportacaoKardex.iniciar("xlsx")}
                />
                <BotaoExportacaoProgresso
                  como="nativo"
                  className={styles.btnHeader}
                  progresso={exportacaoArquivoDigital}
                  rotulo="Baixar arquivo digital"
                  disabled={!podeExportar}
                  onClick={() => exportacaoArquivoDigital.iniciar()}
                />
                <button
                  type="button"
                  className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                  onClick={() => setModalReprocessarAberto(true)}
                >
                  Reprocessar
                </button>
              </div>
            </header>
          </>
        )}

        {mostrarDashboard && (
          <div className={`${styles.dashGrid} ${styles.dashGrid4}`}>
            <div className={`${styles.dashCard} ${styles.dashCardRessarcimento}`}>
              <div className={styles.dashLbl}>Ressarcimento (col. 25)</div>
              <div className={styles.dashSub}>Soma das competências no filtro</div>
              <div className={styles.dashVal}>{formatarMoedaBRL(totais.ressarcimento)}</div>
            </div>
            <div className={`${styles.dashCard} ${styles.dashCardComplemento}`}>
              <div className={styles.dashLbl}>Complemento (col. 26)</div>
              <div className={styles.dashSub}>Soma das competências no filtro</div>
              <div className={styles.dashVal}>{formatarMoedaBRL(totais.complemento)}</div>
            </div>
            <div className={`${styles.dashCard} ${styles.dashCardArt271}`}>
              <div className={styles.dashLbl}>Crédito Art. 271 (col. 27)</div>
              <div className={styles.dashSub}>Código enquadramento legal = 4</div>
              <div className={styles.dashVal}>{formatarMoedaBRL(totais.creditoArt271)}</div>
            </div>
            <div className={`${styles.dashCard} ${styles.dashCardEstoque}`}>
              <div className={styles.dashLbl}>Itens com estoque final</div>
              <div className={styles.dashSub}>Base do registro 1050</div>
              <div className={styles.dashVal}>{formatarQuantidadeLancamentos(itensComEstoqueFinal)}</div>
            </div>
          </div>
        )}

        <div className={styles.sectionListHeader}>
          <h2 className={styles.sectionTitle}>Apuração por competência</h2>
          {/* TXM-418 — quando embutido como aba (mostrarCabecalhoPagina=false), o cabeçalho
              padrão (com os botões) fica oculto pra não duplicar o título já mostrado pela
              tela unificada. Os botões continuam necessários (gerar o arquivo do motor
              preencher_icmsstcat42 pra comparar resultado), então são repetidos aqui, fora
              do cabeçalho — a rota standalone (/detalhe/cat42) não é afetada, continua
              usando só o bloco original. */}
          {!mostrarCabecalhoPagina && (
            <div className={styles.headerActions}>
              <BotaoExportacaoProgresso
                como="nativo"
                className={styles.btnHeader}
                progresso={exportacaoKardex}
                rotulo="Exportar XLSX"
                disabled={!podeExportar}
                onClick={() => exportacaoKardex.iniciar("xlsx")}
              />
              <BotaoExportacaoProgresso
                como="nativo"
                className={styles.btnHeader}
                progresso={exportacaoArquivoDigital}
                rotulo="Baixar arquivo digital"
                disabled={!podeExportar}
                onClick={() => exportacaoArquivoDigital.iniciar()}
              />
              <button
                type="button"
                className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                onClick={() => setModalReprocessarAberto(true)}
              >
                Reprocessar
              </button>
            </div>
          )}
        </div>

        <div className={styles.filtersSection}>
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Competência</span>
              <select
                className={styles.filterSelect}
                value={competenciaFiltro}
                onChange={(e) => setCompetenciaFiltro(e.target.value)}
              >
                <option value="">Todas</option>
                {competenciasDisponiveis.map((ym) => (
                  <option key={ym} value={ym}>
                    {fmtMesAno(ym)}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className={styles.btnPage} onClick={() => setCompetenciaFiltro("")}>
              Limpar filtro
            </button>
          </div>
        </div>

        {porCompetencia.length === 0 ? (
          <p className={styles.emptyMsg}>Nenhuma competência encontrada para esta apuração.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Competência</th>
                  <th scope="col" className={styles.num}>
                    Itens
                  </th>
                  <th scope="col" className={styles.num}>
                    Qtd estoque final
                  </th>
                  <th scope="col" className={styles.num}>
                    ICMS estoque final
                  </th>
                  <th scope="col" className={styles.num}>
                    Ressarcimento
                  </th>
                  <th scope="col" className={styles.num}>
                    Complemento
                  </th>
                  <th scope="col" className={styles.num}>
                    Art. 271
                  </th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {porCompetencia.map((linha) => (
                  <tr key={linha.competencia}>
                    <td>{fmtMesAno(linha.competencia)}</td>
                    <td className={styles.num}>{formatarQuantidadeLancamentos(linha.itens)}</td>
                    <td className={styles.num}>{formatarQuantidadeLancamentos(linha.qtdEstoqueFinal)}</td>
                    <td className={styles.num}>{formatarMoedaBRL(linha.vlEstoqueFinal)}</td>
                    <td className={styles.num} style={{ color: "#2e7d32" }}>{formatarMoedaBRL(linha.ressarcimento)}</td>
                    <td className={styles.num} style={{ color: "#c62828" }}>{formatarMoedaBRL(linha.complemento)}</td>
                    <td className={styles.num}>{formatarMoedaBRL(linha.creditoArt271)}</td>
                    <td>
                      <button
                        type="button"
                        className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                        onClick={() =>
                          navigate(`/relatorios/apuracoes/${id}/detalhe/cat42/lancamentos?competencia=${linha.competencia}`)
                        }
                      >
                        Lançamentos
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalReprocessarCat42
        open={modalReprocessarAberto}
        onClose={() => setModalReprocessarAberto(false)}
        onSuccess={handleReprocessarSucesso}
        onError={handleReprocessarErro}
        apuracao={ap}
        idApuracao={id}
      />

      <Snackbar
        open={infoSnack.open}
        autoHideDuration={4000}
        onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
      </Snackbar>
    </div>
  );
}
